if (!customElements.get('ingredient-feature-carousel')) {
  customElements.define(
    'ingredient-feature-carousel',
    class IngredientFeatureCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.ingredient-product-feature__ingredients-track');
        this.viewport = this.querySelector('.ingredient-product-feature__ingredients-viewport');
        this.source = this.querySelector('[data-ingredient-carousel-source]');
        this.dragOffset = 0;
        this.isDragging = false;
        this.hasDragged = false;
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);

        if (!this.track || !this.viewport || !this.source) {
          return;
        }

        this.bindDrag();
        this.resizeObserver = new ResizeObserver(() => this.refresh());
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.source);
        this.refresh();
      }

      disconnectedCallback() {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }

        this.unbindDrag();
      }

      refresh() {
        const sourceWidth = this.source.getBoundingClientRect().width;
        const viewportWidth = this.getBoundingClientRect().width;
        const trackGap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;

        if (!sourceWidth || !viewportWidth) {
          return;
        }

        this.track
          .querySelectorAll('[data-ingredient-carousel-clone]')
          .forEach((clone) => clone.remove());

        const requiredGroups = Math.max(2, Math.ceil((viewportWidth * 2) / sourceWidth) + 1);

        for (let index = 1; index < requiredGroups; index += 1) {
          const clone = this.source.cloneNode(true);
          clone.removeAttribute('data-ingredient-carousel-source');
          clone.setAttribute('data-ingredient-carousel-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          this.track.appendChild(clone);
        }

        this.groupWidth = sourceWidth + trackGap;
        this.style.setProperty('--ingredient-feature-ingredients-group-width', `${this.groupWidth}px`);
        this.setDragOffset(this.dragOffset);
      }

      bindDrag() {
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('click', this.onClickCapture, true);
      }

      unbindDrag() {
        if (!this.viewport) {
          return;
        }

        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport.removeEventListener('click', this.onClickCapture, true);
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
      }

      onPointerDown(event) {
        if (event.button !== undefined && event.button !== 0) {
          return;
        }

        this.isDragging = true;
        this.hasDragged = false;
        this.startX = event.clientX;
        this.startOffset = this.getCurrentOffset();
        this.classList.add('is-dragging');
        this.track.style.transform = `translate3d(${this.startOffset}px, 0, 0)`;
        this.track.style.animation = 'none';
        this.viewport.setPointerCapture?.(event.pointerId);
        window.addEventListener('pointermove', this.onPointerMove, { passive: false });
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (!this.isDragging) {
          return;
        }

        const delta = event.clientX - this.startX;

        if (Math.abs(delta) > 4) {
          this.hasDragged = true;
        }

        if (this.hasDragged) {
          event.preventDefault();
        }

        this.setDragOffset(this.startOffset + delta);
        this.track.style.transform = `translate3d(${this.dragOffset}px, 0, 0)`;
      }

      onPointerUp() {
        if (!this.isDragging) {
          return;
        }

        this.isDragging = false;
        this.classList.remove('is-dragging');
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
        this.track.style.removeProperty('transform');
        this.track.style.removeProperty('animation');
      }

      onClickCapture(event) {
        if (!this.hasDragged) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.hasDragged = false;
      }

      getCurrentOffset() {
        const transform = getComputedStyle(this.track).transform;

        if (!transform || transform === 'none') {
          return this.dragOffset || 0;
        }

        const matrix = new DOMMatrixReadOnly(transform);
        return matrix.m41 || 0;
      }

      setDragOffset(value) {
        const width = this.groupWidth || 0;

        if (!width) {
          this.dragOffset = value;
        } else {
          this.dragOffset = ((value % width) + width) % width;

          if (this.dragOffset > 0) {
            this.dragOffset -= width;
          }
        }

        this.style.setProperty('--ingredient-feature-ingredients-drag-offset', `${this.dragOffset}px`);
      }
    }
  );
}

const initIngredientProductFeatureReveal = (root = document) => {
  root.querySelectorAll('.ingredient-product-feature:not([data-reveal-bound="true"])').forEach((section) => {
    section.dataset.revealBound = 'true';
    section.classList.add('is-reveal-ready');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      section.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.classList.add('is-visible');
            observer.disconnect();
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.16 }
    );
    observer.observe(section);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initIngredientProductFeatureReveal());
} else {
  initIngredientProductFeatureReveal();
}

document.addEventListener('shopify:section:load', (event) => initIngredientProductFeatureReveal(event.target));
